import { computed, signal } from "@angular/core";

import { Filter } from "./filter";
import type { FilterDefinition, FilterSelectionList, Predicate, SupportedFilterValue, FilterSelectionInput } from "@filter/filter.types";
import { createDefaultSelection, FilterSelectionKind } from "@filter/filter.types";

export abstract class SimpleFilter<TSelection extends FilterSelectionList, TItem> extends Filter<TSelection, TItem, FilterDefinition<TSelection>> {
    protected _selection = signal<TSelection>(this.definition.defaultSelection);

    public readonly isActive = computed(() => {
        const selection = this._selection();
        return "values" in selection ? selection.values.length > 0 : "value" in selection ? Boolean(selection.value) : false;
    });

    protected createDefinition(): FilterDefinition<TSelection> {
        return {
            ...super.createDefinition(),
            kind: FilterSelectionKind.Default,
            defaultSelection: createDefaultSelection(FilterSelectionKind.Default) as TSelection,
        };
    }

    // The selection represents the current state of the filter.
    public abstract addToSelection(value: SupportedFilterValue): void;
    protected abstract normalize(value: SupportedFilterValue): TSelection | undefined;
    public readonly selection = computed(() => this._selection());
    public clear(): void {
        this._selection.set(this.definition.defaultSelection as TSelection);
    }
    public setSelection(value: FilterSelectionInput): void {
        this._selection.set(this.toSelection(value));
    }

    // Accepts a raw UI value or a persisted selection and always resolves to a selection of this filter's own kind.
    private toSelection(value: FilterSelectionInput): TSelection {
        const candidate = value ?? this.definition.defaultSelection;
        const normalized: unknown = this.isSupportedFilterValue(candidate) ? this.normalize(candidate) : candidate;
        return this.isSelectionOfKind(normalized) ? normalized : (this.definition.defaultSelection as TSelection);
    }

    private isSelectionOfKind(value: unknown): value is TSelection {
        return !!value && typeof value === "object" && (value as FilterSelectionList).kind === this.definition.kind;
    }
    protected isSupportedFilterValue(value: any): value is SupportedFilterValue {
        const type = typeof value;
        return (
            type === "string" ||
            type === "number" ||
            value instanceof Date ||
            type === "boolean" ||
            value === null ||
            (Array.isArray(value) && value.every((v) => this.isSupportedFilterValue(v)))
        );
    }

    // The predicates are used to determine if an item satisfies the filter criteria.
    protected abstract getPredicate(selection: TSelection): Predicate<TItem> | null;
    public predicate(): Predicate<TItem> | null {
        const selection = this._selection();
        if ("values" in selection && selection.values.length === 0) return null;
        return this.getPredicate(selection);
    }
}
